<div class="mb-12 mt-12 rounded-lg bg-white shadow-sm">
  @if (isLoading) {
    <div class="flex h-40 items-center justify-center">
      <mat-progress-spinner
        mode="indeterminate"
        color="primary"
        diameter="40"
      ></mat-progress-spinner>
    </div>
  } @else {
    <div class="bg-slate-50 px-4 py-2">
      <div class="flex justify-between">
        <div>
          <mat-form-field
            appearance="outline"
            class="small-search-field min-w-80"
          >
            <mat-icon matPrefix>search</mat-icon>
            <input
              matInput
              (input)="searchTerm$.set($event.target['value']?.toLowerCase())"
              placeholder="Search device"
              autocomplete="off"
            />
          </mat-form-field>
        </div>
        <div class="flex items-center gap-4">
          <div class="flex">
            @for (level of severityCheckboxes | keyvalue; track level.key) {
              <div
                class="inline-block border-b-4 pr-2"
                [ngClass]="{
                  'border-orange-200 hover:border-orange-400':
                    level.key === SeverityLevel.Investigate,
                  'border-yellow-200 hover:border-yellow-400':
                    level.key === SeverityLevel.Monitor,
                  'border-green-200 hover:border-green-400':
                    level.key === SeverityLevel.Observe,
                }"
              >
                <mat-checkbox
                  (change)="level.value.set($event.checked)"
                  [checked]="level.value()"
                  class="rounded-md"
                >
                  {{ level.key }}
                </mat-checkbox>
              </div>
            }
          </div>
        </div>
      </div>
    </div>

    @if (filteredDevices$().length && !isLoading) {
      <div class="flex gap-4 pb-6">
        <!-- Left: Device List -->
        <div class="w-1/3 p-4">
          <ul>
            @for (device of filteredDevices$(); track device.deviceName) {
              <li
                class="flex cursor-pointer items-center justify-between rounded-md border border-gray-300 px-4 py-3 shadow-sm transition hover:bg-blue-50"
                (click)="selectedDevice$.set(device)"
                [ngClass]="[
                  selectedDevice$()?.deviceName === device.deviceName
                    ? 'bg-blue-100'
                    : 'bg-gray-50',
                ]"
              >
                <div>
                  <div class="text-base font-medium text-gray-900">
                    {{ device.deviceName }}
                  </div>
                  <div class="text-sm text-gray-600">
                    {{ device.events.length }}
                    {{ device.events.length || 0 | pluralize: 'Event' }}
                    • Latest event:
                    {{ device.events[0].date | date: 'MMM d, y' }}
                  </div>
                </div>
                <mat-icon class="text-gray-400">chevron_right</mat-icon>
              </li>
            }
          </ul>
        </div>

        <!-- Right: Device Details -->
        <div class="flex-1 p-4">
          <div class="mb-2 pb-4">
            <h3 class="flex justify-between text-2xl font-normal text-gray-800">
              <div>{{ selectedDevice$()?.deviceName || 'None selected' }}</div>
              <div class="text-lg font-light text-gray-600">
                {{ selectedDevice$()?.events.length || 0 }} Observation
                {{
                  selectedDevice$()?.events.length || 0
                    | pluralize: 'Summary' : 'Summaries'
                }}
              </div>
            </h3>
            <p class="text-sm italic text-gray-400">
              (This output is generated using AI and may contain inaccuracies;
              please verify with source data before taking actions)
            </p>
          </div>

          @if (selectedDevice$()?.deviceName) {
            <div class="overflow-x-auto">
              <table
                class="min-w-full table-auto rounded-md border border-gray-200 font-normal"
              >
                <thead class="bg-gray-50">
                  <tr
                    class="text-left text-xs uppercase tracking-wider text-gray-600"
                  >
                    <th class="px-4 py-2">Date</th>
                    <th class="px-4 py-2">Time</th>
                    <th class="px-4 py-2">Severity</th>
                    <th class="px-4 py-2">Lead Count</th>
                  </tr>
                </thead>
                <tbody class="text-sm text-gray-700">
                  @for (
                    event of filteredSelectedDeviceEvents$().slice(
                      pageNumber * 8,
                      (pageNumber + 1) * 8
                    );
                    track $index
                  ) {
                    <tr class="border-t border-gray-100">
                      <td class="px-4 py-2">
                        {{ event.date | date: 'MMM d, y' }}
                      </td>
                      <td class="px-4 py-2">
                        {{ event.begin_time }} – {{ event.end_time }}
                      </td>
                      <td class="px-4 py-2">
                        <span
                          class="inline-block w-20 rounded-full px-2 py-1 text-center text-xs font-medium text-white"
                          [ngClass]="{
                            'bg-orange-500':
                              event.action_label === SeverityLevel.Investigate,
                            'bg-yellow-500':
                              event.action_label === SeverityLevel.Monitor,
                            'bg-green-500':
                              event.action_label === SeverityLevel.Observe,
                          }"
                        >
                          {{ event.action_label }}
                        </span>
                      </td>
                      <td class="px-4 py-2">{{ event.lead_count }}</td>
                    </tr>
                    <tr class="bg-gray-50 text-xs text-gray-600">
                      <td colspan="4" class="px-4 py-2 font-semibold">
                        <div class="flex items-center gap-2">
                          <div
                            class="aspect-square max-h-min rounded-full border-2 border-blue-950"
                          >
                            <mat-icon class="text-blue-950">
                              smart_toy
                            </mat-icon>
                          </div>
                          <div>
                            {{ event.summary }}
                          </div>
                        </div>
                      </td>
                    </tr>
                  }
                </tbody>
              </table>

              <mat-paginator
                (page)="pageNumber = $event.pageIndex"
                [length]="filteredSelectedDeviceEvents$().length"
                [pageIndex]="pageNumber"
                [pageSize]="8"
              ></mat-paginator>
            </div>
          }

          @if (!selectedDevice$()?.deviceName) {
            <p class="text-sm italic text-gray-600">
              Select a device to see details.
            </p>
          }
        </div>
      </div>
    }
  }
</div>
